import { Tooltip, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { ReactComponent as AddNewIcon } from 'common/assets/images/addnew.svg';
import { ReactComponent as RemovedIcon } from 'common/assets/images/removed-minus.svg';
import { Loader } from 'common/components/Loader/Loader';
import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import { SORT_ORDER, TABLE_VIEW_PAGE_SIZE } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	ACTION_TYPE,
	RELATED_OBJECT_TYPE,
} from 'common/constants/TaxonomyConstants';
import { toSentenceCase } from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { getObligationRelationship } from 'features/HorizonScanning/api';
import actionPlanStyles from 'features/HorizonScanning/components/ActionPlanLayout/ActionPlanLayout.module.scss';
import { getMappingIcon } from 'features/HorizonScanning/components/ActionPlanLayout/ActionPlanLayoutUtils';
import { getRelationMapping } from 'features/RiskProfile/Taxonomy/api';

import { sortFieldsHandler } from '../OverviewUtils';

import styles from './RelationshipMappingTable.module.scss';

interface IMappingTabelProps {
	relationId: string;
	relationType: string;
	isPreviewDrawer?: boolean;
	tableDataValues?: any;
	fetchUpdatedTableValues?: (data: any) => void;
}

const { OK } = HTTP_STATUS_CODES;
const { TABLE_SIZE_5, TABLE_SIZE_10 } = TABLE_VIEW_PAGE_SIZE;
const { ADDED_ACTION, REMOVED_ACTION } = ACTION_TYPE;

const RelationMappingTable = (props: IMappingTabelProps) => {
	const {
		relationId,
		relationType,
		isPreviewDrawer,
		tableDataValues,
		fetchUpdatedTableValues,
	} = props;
	const pageSize = isPreviewDrawer ? TABLE_SIZE_5 : TABLE_SIZE_10;

	/* Hooks */
	const { tableQueryObj, updateTableQuery } = useTableQuery({
		id: 'relationMappingTable',
		defaultPageSize: pageSize,
	});

	/* Local states */
	const [tableData, setTableData] = useState<any>([]);
	const [isLoading, setLoading] = useState<boolean>(false);
	const [totalRecordsCount, setTotalRecordCount] = useState<number>(0);
	const [totalPagesCount, setTotalPagesCount] = useState<number>(0);
	const [sortOrder, setSortOrder] = useState<string>(SORT_ORDER.ASCENDING);
	const { iconFillColor } = styles;

	/* Local Variables */
	const columns: readonly Column[] = [
		{
			id: 'childName',
			label: 'Name',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => {
				let prefix = null;

				if (params?.actionType === ADDED_ACTION) {
					prefix = (
						<div className={styles['added-prefix']}>
							<AddNewIcon
								fill={iconFillColor}
								className={styles['added-icon']}
							/>
							<Typography className={styles['added-text']}>
								{ADDED_ACTION}:
							</Typography>
						</div>
					);
				} else if (params?.actionType === REMOVED_ACTION) {
					prefix = (
						<div className={styles['added-prefix']}>
							<RemovedIcon
								fill={iconFillColor}
								className={styles['added-icon']}
							/>
							<Typography className={styles['removed-text']}>
								{REMOVED_ACTION}:
							</Typography>
						</div>
					);
				}

				return (
					<Typography className={styles.name}>
						{prefix}
						<Tooltip title={params?.childName} placement="bottom-start">
							<div className={styles['child-name']}>
								{params?.childName || ''}
							</div>
						</Tooltip>
					</Typography>
				);
			},
		},
		{
			id: 'childType',
			label: 'Type',
			isSortable: true,
			disablePadding: false,
			render: (params: any) => (
				<Typography className={styles.type}>
					{!isPreviewDrawer && (
						<span className={styles['icon-spacing']}>
							<div
								className={`${
									actionPlanStyles[`${getMappingIcon(params?.childType)}`]
								} ${styles['circle-icon']}`}
							/>
						</span>
					)}
					<Tooltip title={params?.childType} placement="bottom-start">
						<span>{params?.childType}</span>
					</Tooltip>
				</Typography>
			),
		},
	];

	/**
	 * @description method to trigger api call for getting all relation mapping
	 * @param params
	 */
	const fetchRelationMappingData = async (params: any) => {
		setLoading(true);
		let res: any;
		if (
			relationType?.toLowerCase() === RELATED_OBJECT_TYPE.GROUP.toLowerCase()
		) {
			res = await getObligationRelationship(relationId, params);
		} else {
			const payloadRelationType = toSentenceCase(relationType);
			res = await getRelationMapping(
				{ relationId, relationType: payloadRelationType },
				params,
			);
		}
		setLoading(false);
		if (res?.statusCode === OK) {
			const responseData = res?.responseBody;
			setTableData(responseData?.content);
			setTotalRecordCount(responseData?.totalCount);
			setTotalPagesCount(responseData?.totalPages);
		} else {
			setTableData([]);
		}
	};

	/**
	 * @description method to handle sorting within the table
	 * @param data
	 */
	const handleSortChange = (data: any) => {
		const currentSortOrder = sortFieldsHandler(sortOrder);
		setSortOrder(currentSortOrder);
		if (fetchUpdatedTableValues && tableDataValues) {
			fetchUpdatedTableValues(data);
		} else {
			fetchRelationMappingData(data);
		}
	};

	/**
	 * @description method to handle pagination within the table
	 * @param data
	 * @param flag
	 */
	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag && !tableDataValues) {
			fetchRelationMappingData(data);
		} else if (fetchUpdatedTableValues) {
			fetchUpdatedTableValues(data);
		}
		updateTableQuery(data);
	};

	/**
	 * @description method to render message when no data is found or loader
	 */
	const renderNoDataImage = () =>
		isLoading ? (
			<Loader show className={styles['relation-mapping-loader']} />
		) : (
			<p className={styles['no-activity-text']}>
				{toSentenceCase('No relationship mapping found')}
			</p>
		);

	useEffect(() => {
		if (!tableDataValues) {
			fetchRelationMappingData(tableQueryObj);
		} else {
			setTableData(tableDataValues?.content);
			setTotalRecordCount(tableDataValues?.totalCount);
			setTotalPagesCount(tableDataValues?.totalPages);
		}
	}, [tableDataValues]);

	return (
		<div>
			{tableData && tableData?.length > 0 ? (
				<TableComponent
					defaultQuery={tableQueryObj}
					rows={tableData}
					columns={columns}
					totalItems={totalRecordsCount}
					defaultSortKey=""
					onPageHandler={handlePageChange}
					isLoading={isLoading}
					tableStyles={{ minWidth: 1, maxHeight: '7.5em' }}
					totalPagesCount={totalPagesCount}
					enableHeaderColor
					onSortHandler={handleSortChange}
					isRenderPagination={totalRecordsCount > pageSize}
					hideSelectPageOption
					isFlatStyle
				/>
			) : (
				renderNoDataImage()
			)}
		</div>
	);
};

export default RelationMappingTable;
